/**
 * RequirementsList Component
 * 
 * List of requirements with must-match grouping and drag-and-drop between sections
 */

import React, { useMemo, useState, useEffect } from 'react';
import { Alert, Empty, Typography, Badge, message } from 'antd';
import RequirementCard from './RequirementCard';
import { RequirementState } from '../utils/formTransformers';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useCountUp } from '../../../../../Utils/countUpAnimation';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const { Title } = Typography;

interface RequirementsListProps {
  requirements: RequirementState[];
  onReorder: (newRequirements: RequirementState[]) => void;
  onEdit: (requirement: RequirementState) => void;
  onDelete: (id: string | number) => void;
  onToggleMustMatch: (id: string | number, mustMatch: boolean) => void;
  mustMatchCount: number;
  isEditMode?: boolean;
  originalRequirementIds?: Set<string | number>;
}

/**
 * Droppable Section Component
 */
const DroppableSection: React.FC<{
  id: string;
  children: React.ReactNode;
}> = ({ id, children }) => {
  const { setNodeRef } = useDroppable({
    id,
  });

  return <div ref={setNodeRef}>{children}</div>;
};

/**
 * Sortable Requirement Card Item
 */
const SortableRequirementCard: React.FC<{
  requirement: RequirementState;
  index: number;
  onEdit: (requirement: RequirementState) => void;
  onDelete: (id: string | number) => void;
  onToggleMustMatch: (id: string | number, mustMatch: boolean) => void;
  isTransitioning: boolean;
  isLeaving?: boolean;
  mustMatchCount: number;
  isAlreadyAdded: boolean;
  sectionType: 'must-match' | 'optional';
}> = ({
  requirement,
  index,
  onEdit,
  onDelete,
  onToggleMustMatch,
  isTransitioning,
  isLeaving,
  mustMatchCount,
  isAlreadyAdded,
  sectionType,
}) => {
  const sortableId = String(requirement.id);
  
  // Disable sorting for existing requirements in edit mode (read-only)
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: sortableId,
    disabled: isAlreadyAdded, // Disable drag for published requirements
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 8,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <RequirementCard
        requirement={requirement}
        index={index}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleMustMatch={onToggleMustMatch}
        isTransitioning={isTransitioning || isDragging}
        isLeaving={isLeaving}
        mustMatchCount={mustMatchCount}
        isAlreadyAdded={isAlreadyAdded}
        // Don't pass drag handle props for read-only requirements
        dragHandleProps={isAlreadyAdded ? undefined : { ...attributes, ...listeners }}
        isDragging={isDragging}
      />
    </div>
  );
};

const RequirementsList: React.FC<RequirementsListProps> = ({
  requirements,
  onReorder,
  onEdit,
  onDelete,
  onToggleMustMatch,
  mustMatchCount,
  isEditMode = false,
  originalRequirementIds = new Set(),
}) => {
  // Track transitioning cards to prevent instant removal
  const [transitioningIds, setTransitioningIds] = useState<Set<string | number>>(new Set());
  const [prevRequirements, setPrevRequirements] = useState<RequirementState[]>(requirements);

  // Configure sensors for drag and drop
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag activates (allows clicks on buttons)
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Separate requirements into must-match and non-must-match
  const { mustMatchRequirements, nonMustMatchRequirements } = useMemo(() => {
    const mustMatch: RequirementState[] = [];
    const nonMustMatch: RequirementState[] = [];

    for (let index = 0; index < requirements.length; index++) {
      const req = requirements[index];
      const requirementWithOrder = { ...req, order: index };
      if (req.mustMatch) {
        mustMatch.push(requirementWithOrder);
      } else {
        nonMustMatch.push(requirementWithOrder);
      }
    }

    return {
      mustMatchRequirements: mustMatch,
      nonMustMatchRequirements: nonMustMatch,
    };
  }, [requirements]);

  // Detect when a requirement changes sections and handle transitions
  useEffect(() => {
    const transitioning = new Set<string | number>();
    const timeoutIds: NodeJS.Timeout[] = [];
    
    for (const req of requirements) {
      const prevReq = prevRequirements.find(p => p.id === req.id);
      if (prevReq && prevReq.mustMatch !== req.mustMatch) {
        // Card is moving between sections
        transitioning.add(req.id);
        // Remove from transitioning after animation completes
        const reqId = req.id;
        const timeoutId = setTimeout(() => {
          setTransitioningIds(prev => {
            const next = new Set(prev);
            next.delete(reqId);
            return next;
          });
        }, 300); // Match CSS transition duration
        timeoutIds.push(timeoutId);
      }
    }

    if (transitioning.size > 0) {
      setTransitioningIds(transitioning);
    }
    setPrevRequirements(requirements);

    // Cleanup timeouts on unmount to prevent memory leaks
    return () => {
      timeoutIds.forEach(id => clearTimeout(id));
    };
  }, [requirements, prevRequirements]);

  // Animated counts for section titles
  const animatedMustMatchCount = useCountUp(mustMatchRequirements.length, 600, 0, 'easeOut');
  const optionalCount = useCountUp(nonMustMatchRequirements.length, 600, 0, 'easeOut');

  // Generate IDs for sortable context
  const mustMatchIds = useMemo(() => {
    return mustMatchRequirements.map(req => String(req.id));
  }, [mustMatchRequirements]);

  const optionalIds = useMemo(() => {
    return nonMustMatchRequirements.map(req => String(req.id));
  }, [nonMustMatchRequirements]);

  // Count how many must-match requirements are draggable (not read-only in edit mode)
  const draggableMustMatchCount = useMemo(() => {
    return mustMatchRequirements.filter(req => 
      !(isEditMode && originalRequirementIds.has(req.id))
    ).length;
  }, [mustMatchRequirements, isEditMode, originalRequirementIds]);

  // Handle drag end - toggle mustMatch when dropped in different section
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      return;
    }

    // Find the requirement being dragged
    const draggedRequirement = requirements.find(req => String(req.id) === active.id);
    if (!draggedRequirement) {
      return;
    }

    // Determine which section the requirement was dropped in
    let targetSection: 'must-match' | 'optional' | null = null;
    
    if (over.id === 'must-match-section') {
      targetSection = 'must-match';
    } else if (over.id === 'optional-section') {
      targetSection = 'optional';
    } else {
      // Dropped on another item - find which section that item belongs to
      const targetRequirement = requirements.find(req => String(req.id) === over.id);
      if (targetRequirement) {
        targetSection = targetRequirement.mustMatch ? 'must-match' : 'optional';
      }
    }

    if (!targetSection) {
      return;
    }

    // Determine source section
    const sourceSection = draggedRequirement.mustMatch ? 'must-match' : 'optional';

    // If dropped in a different section, toggle mustMatch
    if (targetSection !== sourceSection) {
      if (targetSection === 'must-match' && !draggedRequirement.mustMatch) {
        // Moving from optional to must-match
        // Check validation: at least one must-match is required, but we're adding one, so it's fine
        onToggleMustMatch(draggedRequirement.id, true);
      } else if (targetSection === 'optional' && draggedRequirement.mustMatch) {
        // Moving from must-match to optional
        // Check validation: ensure we don't remove the last must-match
        if (mustMatchCount > 1) {
          onToggleMustMatch(draggedRequirement.id, false);
        } else {
          // Provide feedback when user tries to move the last must-match requirement
          message.warning('At least one Must Match requirement is required');
        }
      }
    }
    // If dropped in the same section, do nothing (no reordering within sections for now)
  };

  if (requirements.length === 0) {
    return (
      <Empty
        description={
          <Typography.Text type="secondary">
            No requirements added yet. Click "Add Requirement" to get started.
          </Typography.Text>
        }
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  // Check if we should show sections (including transitioning cards)
  const shouldShowMustMatch = mustMatchRequirements.length > 0 || 
    Array.from(transitioningIds).some(id => {
      const req = prevRequirements.find(r => r.id === id);
      return req?.mustMatch;
    });

  const shouldShowOptional = nonMustMatchRequirements.length > 0 || 
    Array.from(transitioningIds).some(id => {
      const req = prevRequirements.find(r => r.id === id);
      return req && !req.mustMatch;
    }) ||
    // Show empty drop zone when must-match items exist that CAN be moved:
    // - More than 1 must-match total (to keep at least one)
    // - At least one is draggable (not read-only)
    (mustMatchRequirements.length > 0 && mustMatchCount > 1 && draggableMustMatchCount > 0);

  return (
    <div className="requirements-list">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        {/* Must Match Section */}
        {shouldShowMustMatch && (
          <DroppableSection id="must-match-section">
            <div className="requirements-section requirements-section--must-match" style={{ marginBottom: 24 }}>
              <Title level={5} style={{ marginTop: 0, marginBottom: 8, fontWeight: 600 }}>
                Must Match Requirements
                <Badge 
                  count={animatedMustMatchCount.count} 
                  showZero 
                  style={{ marginLeft: 8 }}
                  className="requirement-count-badge requirement-count-badge--must-match"
                />
              </Title>
              <Alert 
                type="info" 
                icon={<InfoCircleOutlined />}
                message="These are critical requirements that candidates must meet to be considered for the role. 
                Candidates who don't meet all 'Must Match' requirements will not be matched to this job post. 
                Use this for essential skills, qualifications, or experience that are non-negotiable."
              />
              <SortableContext items={mustMatchIds} strategy={verticalListSortingStrategy}>
                {/* Show current must-match requirements, excluding those transitioning out */}
                {mustMatchRequirements
                  .filter(req => {
                    // Get the current state of this requirement from the requirements prop
                    const currentReq = requirements.find(r => r.id === req.id);
                    // Exclude if: requirement is transitioning AND current state is non-must-match
                    // This handles the case where the requirement was just toggled off
                    if (!currentReq) {
                      // Requirement not found in current requirements, exclude it
                      return false;
                    }
                    // Only show if it's currently must-match (not transitioning out)
                    // If it's transitioning out, it will be shown as a transitioning card below
                    const isTransitioningOut = transitioningIds.has(req.id) && !currentReq.mustMatch;
                    return !isTransitioningOut && currentReq.mustMatch;
                  })
                  .map((req, index) => {
                    // For edit mode: true if requirement ID is in original set, false otherwise
                    // For new/clone mode: always false
                    const isAlreadyAdded = isEditMode && originalRequirementIds.has(req.id);
                    return (
                      <SortableRequirementCard
                        key={req.id}
                        requirement={req}
                        index={index}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onToggleMustMatch={onToggleMustMatch}
                        isTransitioning={transitioningIds.has(req.id)}
                        mustMatchCount={mustMatchCount}
                        isAlreadyAdded={isAlreadyAdded}
                        sectionType="must-match"
                      />
                    );
                  })}
                {/* Show transitioning cards that are leaving this section (for animation) */}
                {prevRequirements
                  .filter(req => {
                    const currentReq = requirements.find(r => r.id === req.id);
                    // Only show if it was must-match before, is now non-must-match, and is transitioning
                    return req.mustMatch && currentReq && !currentReq.mustMatch && transitioningIds.has(req.id);
                  })
                  .map((req, index) => {
                    const isAlreadyAdded = isEditMode && originalRequirementIds.has(req.id);
                    return (
                      <RequirementCard
                        key={`transitioning-out-${req.id}`}
                        requirement={req}
                        index={index}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onToggleMustMatch={onToggleMustMatch}
                        isTransitioning={true}
                        isLeaving={true}
                        mustMatchCount={mustMatchCount}
                        isAlreadyAdded={isAlreadyAdded}
                      />
                    );
                  })}
              </SortableContext>
            </div>
          </DroppableSection>
        )}

        {/* Non Must Match Section */}
        {shouldShowOptional && (
          <DroppableSection id="optional-section">
            <div className="requirements-section requirements-section--optional">
              <Title level={5} style={{ marginBottom: 12, fontWeight: 600 }}>
                Other Requirements
                <Badge 
                  count={optionalCount.count} 
                  showZero 
                  style={{ marginLeft: 8 }}
                  className="requirement-count-badge requirement-count-badge--optional"
                />
              </Title>
              <SortableContext items={optionalIds} strategy={verticalListSortingStrategy}>
                {/* Empty drop zone when no optional requirements but items can be moved */}
                {nonMustMatchRequirements.length === 0 && !Array.from(transitioningIds).some(id => {
                  const req = prevRequirements.find(r => r.id === id);
                  return req && !req.mustMatch;
                }) && (
                  <div 
                    style={{ 
                      padding: '24px 16px', 
                      border: '2px dashed var(--ant-color-border)', 
                      borderRadius: 8,
                      textAlign: 'center',
                      color: 'var(--ant-color-text-secondary)',
                      backgroundColor: 'var(--ant-color-bg-container-disabled)',
                      marginBottom: 8,
                    }}
                  >
                    <Typography.Text type="secondary">
                      Drag requirements here to mark them as optional
                    </Typography.Text>
                  </div>
                )}
                {nonMustMatchRequirements.map((req, index) => {
                  const isAlreadyAdded = isEditMode && originalRequirementIds.has(req.id);
                  return (
                    <SortableRequirementCard
                      key={req.id}
                      requirement={req}
                      index={mustMatchRequirements.length + index}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onToggleMustMatch={onToggleMustMatch}
                      isTransitioning={transitioningIds.has(req.id)}
                      mustMatchCount={mustMatchCount}
                      isAlreadyAdded={isAlreadyAdded}
                      sectionType="optional"
                    />
                  );
                })}
                {/* Show transitioning cards that are leaving this section */}
                {prevRequirements
                  .filter(req => {
                    const currentReq = requirements.find(r => r.id === req.id);
                    return !req.mustMatch && currentReq?.mustMatch && transitioningIds.has(req.id);
                  })
                  .map((req, index) => {
                    const isAlreadyAdded = isEditMode && originalRequirementIds.has(req.id);
                    return (
                      <RequirementCard
                        key={`transitioning-${req.id}`}
                        requirement={req}
                        index={mustMatchRequirements.length + index}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        onToggleMustMatch={onToggleMustMatch}
                        isTransitioning={true}
                        isLeaving={true}
                        mustMatchCount={mustMatchCount}
                        isAlreadyAdded={isAlreadyAdded}
                      />
                    );
                  })}
              </SortableContext>
            </div>
          </DroppableSection>
        )}
      </DndContext>
    </div>
  );
};

export default RequirementsList;

